import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Users, UserPlus, UserMinus, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const MetricCard = ({ label, value, sub, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-3">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    {trend != null && (
      <div className={`flex items-center gap-0.5 mt-1 text-[10px] font-semibold ${trend >= 0 ? "text-emerald-600" : "text-red-500"}`}>
        {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {Math.abs(trend)}%
      </div>
    )}
  </div>
);

const PIE_COLORS = ["#10b981", "#f59e0b", "#ef4444"];

export default function RetentionDashboardPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/analytics/retention-dashboard`);
        setData(res.data);
      } catch (e) { console.error("Retention dashboard error:", e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-6">No data available</p>;

  const pieData = [
    { name: "Returning", value: data.returning_users_this_week },
    { name: "New", value: data.new_users_this_week },
    { name: "Churned", value: data.churned_count },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-4" data-testid="retention-dashboard-panel">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-2" data-testid="retention-kpi-cards">
        <MetricCard label="DAU" value={data.dau_today} sub="Today" icon={Users} color="text-sky-600" />
        <MetricCard label="WAU" value={data.wau} sub="Last 7 days" icon={Users} color="text-indigo-600" />
        <MetricCard label="MAU" value={data.mau} sub="Last 30 days" icon={Users} color="text-violet-600" />
        <MetricCard label="Total Users" value={data.total_users} icon={Users} color="text-slate-700" />
      </div>

      {/* Retention vs Churn */}
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="Retention" value={`${data.retention_rate}%`} sub="Week over week" icon={TrendingUp} color="text-emerald-600" />
        <MetricCard label="Churn" value={`${data.churn_rate}%`} sub={`${data.churned_count} users`} icon={UserMinus} color="text-red-500" />
      </div>

      {/* New vs Returning pie */}
      <div className="bg-white rounded-xl border border-slate-100 p-3">
        <p className="text-xs font-semibold text-slate-700 mb-2">New vs Returning (This Week)</p>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={20} outerRadius={38} paddingAngle={3} dataKey="value">
                  {pieData.map((_, idx) => (
                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} users`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5">
            {pieData.map((d, idx) => (
              <div key={d.name} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                <span className="text-xs text-slate-600">{d.name}: <b>{d.value}</b></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DAU Trend Chart */}
      <div className="bg-white rounded-xl border border-slate-100 p-3">
        <p className="text-xs font-semibold text-slate-700 mb-2">Daily Active Users (14 days)</p>
        <div className="h-40" data-testid="dau-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.dau_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip labelStyle={{ fontSize: 11 }} itemStyle={{ fontSize: 11 }} />
              <Bar dataKey="dau" fill="#0ea5e9" radius={[3, 3, 0, 0]} name="Users" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Activity Heatmap by Day of Week */}
      {data.activity_heatmap?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2">Activity by Day of Week</p>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.activity_heatmap}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" allowDecimals={false} />
                <Tooltip labelStyle={{ fontSize: 11 }} itemStyle={{ fontSize: 11 }} />
                <Bar dataKey="actions" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="Actions" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Cohort Retention Table */}
      {data.cohorts?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 p-3 overflow-x-auto">
          <p className="text-xs font-semibold text-slate-700 mb-2">Weekly Cohort Retention</p>
          <table className="w-full text-xs" data-testid="cohort-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-1.5 px-2 text-slate-500 font-medium">Cohort</th>
                <th className="text-center py-1.5 px-2 text-slate-500 font-medium">Size</th>
                {data.cohorts[0]?.retained?.map((_, i) => (
                  <th key={i} className="text-center py-1.5 px-2 text-slate-500 font-medium">Wk {i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.cohorts.map((c) => (
                <tr key={c.week} className="border-b border-slate-50">
                  <td className="py-1.5 px-2 font-medium text-slate-700">{c.week}</td>
                  <td className="py-1.5 px-2 text-center text-slate-600">{c.signed_up}</td>
                  {c.retained.map((pct, i) => (
                    <td key={i} className="py-1.5 px-2 text-center">
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold"
                        style={{
                          backgroundColor: `rgba(16, 185, 129, ${Math.max(pct / 100, 0.05)})`,
                          color: pct >= 50 ? "white" : "#065f46",
                        }}
                      >
                        {pct}%
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
